import React, { useCallback, useEffect, useRef, useState } from "react";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Spin, Tooltip, TreeSelect, message } from "antd";
import { addSprTovDeny, fetchSprTovDeny, fetchSprTovList, removeSprTovDeny } from "./spr-tov-api";

const toIsGroup = (row) => Number(row.isGroup ?? row.IsGroup) === 1;
const toIsHide = (row) => Number(row.isHide ?? row.IsHide) === 1;

/**
 * Вкладка «Совместимость»: услуги, несовместимые с текущей (SPR_TOV_DENY).
 */
const SprTovDeny = ({ idTov }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picking, setPicking] = useState(false);
  const [treeData, setTreeData] = useState([]);
  const requestIdRef = useRef(0);

  const load = useCallback(() => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    fetchSprTovDeny(idTov)
      .then((data) => {
        if (requestId === requestIdRef.current) setItems(Array.isArray(data) ? data : []);
      })
      .catch((e) => {
        if (requestId === requestIdRef.current) {
          message.error(e?.message || "Не удалось загрузить совместимость");
        }
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoading(false);
      });
  }, [idTov]);

  useEffect(() => {
    setPicking(false);
    load();
  }, [load]);

  const startPick = useCallback(() => {
    setPicking(true);
    fetchSprTovList()
      .then((data) => {
        const list = (Array.isArray(data) ? data : [])
          .map((r) => ({
            idTov: Number(r.idTov ?? r.IdTov),
            idUpper: Number(r.idUpper ?? r.IdUpper) || 0,
            isGroup: toIsGroup(r),
            isHide: toIsHide(r),
            name: r.name ?? r.Name ?? ""
          }))
          .filter((r) => r.idTov !== idTov && (r.isGroup || !r.isHide));
        const byParent = new Map();
        list.forEach((r) => {
          const bucket = byParent.get(r.idUpper) || [];
          bucket.push(r);
          byParent.set(r.idUpper, bucket);
        });
        const build = (parentId) =>
          (byParent.get(parentId) || [])
            .sort((a, b) => a.name.localeCompare(b.name, "ru"))
            .map((r) => {
              const children = r.isGroup ? build(r.idTov) : [];
              return {
                value: r.idTov,
                title: r.name || `№${r.idTov}`,
                ...(children.length ? { children } : {})
              };
            });
        setTreeData(build(0));
      })
      .catch((e) => message.error(e?.message || "Не удалось загрузить список услуг"));
  }, [idTov]);

  const onPick = useCallback(
    (id) => {
      if (id == null) return;
      addSprTovDeny(idTov, { idTovDeny: id })
        .then((list) => {
          setItems(Array.isArray(list) ? list : []);
          setPicking(false);
        })
        .catch((e) => message.error(e?.message || "Не удалось добавить"));
    },
    [idTov]
  );

  const remove = useCallback(
    (row) => {
      removeSprTovDeny(idTov, row.idTovDeny)
        .then(() => setItems((prev) => prev.filter((r) => r.idTovDeny !== row.idTovDeny)))
        .catch((e) => message.error(e?.message || "Не удалось удалить"));
    },
    [idTov]
  );

  return (
    <div className="spr-tov-card__tab">
      <div className="spr-tov-card__body spr-tov-deny">
        <div className="spr-tov-params__head">
          <span className="spr-tov-params__title">Эта процедура не совместима с…</span>
          {picking ? (
            <TreeSelect
              autoFocus
              open
              showSearch
              treeNodeFilterProp="title"
              popupMatchSelectWidth={false}
              treeDefaultExpandAll={false}
              treeData={treeData}
              placeholder="Услуга или группа…"
              className="spr-tov-deny__picker"
              size="small"
              styles={{ popup: { root: { maxWidth: 420 } } }}
              value={null}
              onChange={onPick}
              onBlur={() => setPicking(false)}
            />
          ) : (
            <Tooltip title="Добавить">
              <Button
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                aria-label="Добавить"
                onClick={startPick}
              />
            </Tooltip>
          )}
        </div>
        <div className="spr-tov-params__list">
          {loading ? (
            <div className="spr-tov-params__center">
              <Spin size="small" />
            </div>
          ) : items.length ? (
            items.map((row) => (
              <div key={row.idTovDeny} className="spr-tov-params__row">
                <span className="spr-tov-params__row-text" title={row.name}>
                  {row.name || `№${row.idTovDeny}`}
                </span>
                <span className="spr-tov-params__row-actions">
                  <Tooltip title="Удалить">
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<CloseOutlined />}
                      onClick={() => remove(row)}
                    />
                  </Tooltip>
                </span>
              </div>
            ))
          ) : (
            <div className="spr-tov-params__empty">Нет несовместимых процедур</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SprTovDeny;
